import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Check, LoaderCircle, PhoneOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { displayName, formatPhone, formatStep, formatTime } from '@/lib/conversationFormat';
import { callTimeline, type CallLine, type LiveCallState } from '@/lib/liveCall';
import conversationService, { type ConversationDetailResponse } from '@/services/conversationService';
import ChatHeader, { LiveMark } from './ChatHeader';
import { CallContext, type AgentLabel } from './ContextPanel';
import MessageBubble from './MessageBubble';
import { ElapsedClock } from './TimeText';
import { useLiveCall } from './useLiveCall';
import { ChatError, ChatSkeleton, WorkspaceFrame, type WorkspaceChrome } from './WorkspaceFrame';

type Props = WorkspaceChrome & {
  callSid: string;
  me: string | null;
  agentLabel?: AgentLabel;
  /** The call ended and its transcript is stored: switch to it (preloaded when possible). */
  onEnded: (conversationId: number, preloaded: ConversationDetailResponse | null) => void;
};

const NEAR_BOTTOM_PX = 96;

function PartialLine({ line }: { line: CallLine }) {
  const isCustomer = line.role === 'user';
  return (
    <div
      className={cn('mt-2.5 flex w-full', isCustomer ? 'justify-start pr-10 sm:pr-16' : 'justify-end pl-10 sm:pl-16')}
      data-partial={line.seq}
    >
      <div
        className={cn(
          'max-w-[34rem] rounded-2xl border border-dashed px-3.5 pb-1.5 pt-2 text-[14px] leading-relaxed text-foreground/80',
          isCustomer ? 'border-border bg-card' : 'border-brand/35 bg-brand-soft',
        )}
      >
        <span className="mb-0.5 flex items-center gap-1.5 text-[10.5px] font-semibold text-muted-foreground">
          <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-60 motion-safe:animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
          </span>
          {isCustomer ? 'Kunde spricht …' : 'Agent spricht …'}
        </span>
        <p className="whitespace-pre-wrap break-words">{line.content}</p>
      </div>
    </div>
  );
}

function StepLine({ label, ts, active }: { label: string; ts: string | null; active: boolean }) {
  const text = formatStep(label, { running: active });
  if (!text) return null;
  const time = formatTime(ts);
  return (
    <div className="flex justify-center py-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-1.5 text-[11.5px]',
          active ? 'font-medium text-foreground/80' : 'text-muted-foreground',
        )}
      >
        {active ? (
          <LoaderCircle className="h-3 w-3 text-brand motion-safe:animate-spin" aria-hidden="true" />
        ) : (
          <Check className="h-3 w-3" aria-hidden="true" />
        )}
        <span className="italic">{text}</span>
        {time && <span className="tabular-nums text-foreground/55">{time}</span>}
      </span>
    </div>
  );
}

/** The live transcript: finished lines as bubbles, the growing line dashed, tool steps quiet. */
function CallTranscript({ state }: { state: LiveCallState }) {
  const entries = callTimeline(state);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const last = entries[entries.length - 1];
  const lastSignature = last ? `${last.key}:${last.type === 'turn' ? last.turn.content.length : ''}` : '';

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [entries.length, lastSignature]);

  let previousRole: string | null = null;
  const rows: ReactNode[] = entries.map((entry) => {
    if (entry.type === 'step') {
      previousRole = null;
      return <StepLine key={entry.key} label={entry.step.label} ts={entry.step.ts} active={entry.active} />;
    }
    const line = entry.turn;
    if (line.partial) {
      previousRole = null;
      // Not part of the polite log: it changes several times a second.
      return (
        <div key={`partial-${line.seq}`} aria-hidden="true">
          <PartialLine line={line} />
        </div>
      );
    }
    const groupStart = previousRole !== line.role;
    previousRole = line.role;
    return (
      <MessageBubble
        key={`turn-${line.seq}`}
        channel="phone"
        message={{ role: line.role, content: line.content, ts: line.ts }}
        groupStart={groupStart}
      />
    );
  });

  return (
    <div
      ref={scrollerRef}
      onScroll={() => {
        const el = scrollerRef.current;
        if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      }}
      className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain bg-chrome/60 px-3 pb-4 pt-3 sm:px-6"
      style={{
        backgroundImage: 'radial-gradient(hsl(var(--foreground) / 0.045) 1px, transparent 1.2px)',
        backgroundSize: '18px 18px',
      }}
    >
      <div className="mx-auto flex max-w-3xl flex-col" role="log" aria-live="polite" aria-label="Live-Transkript">
        {entries.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">Noch nichts gesagt – das Transkript erscheint hier live.</p>
        ) : (
          rows
        )}
      </div>
    </div>
  );
}

const STATUS_TEXT = { live: 'läuft', transferring: 'wird weitergeleitet', ended: 'beendet' } as const;

/**
 * A phone call while it runs (livecenter §5, read only): the transcript grows,
 * tool steps appear as quiet lines, and when the call ends the stored
 * transcript takes over seamlessly.
 */
export function CallWorkspace({ callSid, agentLabel, onEnded, layout, contextOpen, onContextOpenChange, onBack }: Props) {
  const headingId = useId();
  const { state, loading, error, storedMissing, reload } = useLiveCall(callSid);
  const endedRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  });

  const status = state?.call.status;
  const conversationId = state?.call.conversation_id ?? null;
  useEffect(() => {
    if (status !== 'ended' || conversationId == null || endedRef.current) return;
    endedRef.current = true;
    let cancelled = false;
    conversationService
      .getDetail(conversationId)
      .then(
        (detail) => detail,
        () => null,
      )
      .then((detail) => {
        if (!cancelled) onEndedRef.current(conversationId, detail);
      });
    return () => {
      cancelled = true;
      endedRef.current = false;
    };
  }, [status, conversationId]);

  if (loading && !state) return <ChatSkeleton onBack={onBack} />;
  if (!state) return <ChatError message={error ?? 'Anruf nicht gefunden.'} onRetry={reload} onBack={onBack} />;

  const call = state.call;
  const running = call.status !== 'ended';
  const agentLine = call.agent_number ? agentLabel?.(call.agent_number) ?? formatPhone(call.agent_number) : null;

  const chat = (
    <>
      <ChatHeader
        headingId={headingId}
        name={displayName(call.customer_name, call.customer_phone)}
        avatarName={call.customer_name}
        avatarPhone={call.customer_phone}
        live={running}
        phoneCall
        badges={
          running ? (
            <LiveMark />
          ) : (
            <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <PhoneOff className="h-3 w-3" aria-hidden="true" />
              Beendet
            </span>
          )
        }
        meta={
          <>
            {call.customer_name?.trim() && <>{formatPhone(call.customer_phone)} · </>}
            {agentLine && <>über {agentLine} · </>}
            Telefon · {STATUS_TEXT[call.status]} ·{' '}
            <ElapsedClock since={call.started_at} until={call.ended_at ?? null} />
          </>
        }
        onBack={onBack}
        onToggleContext={() => onContextOpenChange(!contextOpen)}
        contextOpen={contextOpen}
      />
      {!running && (
        <p role="status" className="flex-shrink-0 border-b border-border bg-muted/60 px-5 py-2 text-xs text-muted-foreground">
          {call.conversation_id != null
            ? 'Anruf beendet – der gespeicherte Verlauf wird geöffnet …'
            : storedMissing
              ? 'Anruf beendet. Der gespeicherte Verlauf ist noch nicht da – er erscheint in der Liste unter „Telefon“, sobald er gespeichert ist.'
              : 'Anruf beendet – der Verlauf wird gerade gespeichert …'}
        </p>
      )}
      <CallTranscript state={state} />
      {running && (
        <p className="flex-shrink-0 border-t border-border bg-card px-5 py-2.5 text-center text-xs text-muted-foreground">
          Nur mitlesen – der Agent führt das Gespräch.
        </p>
      )}
    </>
  );

  return (
    <WorkspaceFrame
      layout={layout}
      contextOpen={contextOpen}
      onContextOpenChange={onContextOpenChange}
      onBack={onBack}
      labelledBy={headingId}
      chat={chat}
      context={<CallContext call={call} agentLabel={agentLabel} />}
    />
  );
}

export default CallWorkspace;
